const ACTIVE_FOLDERS_KEY = "reservationPlanFolders";
const ARCHIVED_FOLDERS_KEY = "reservationPlanFoldersArchived";
const FLOORS_KEY = "reservationPlanFloors";
const MAX_FOLDERS_PER_SALLE = 8;

const safeParse = (raw, fallback) => {
  try {
    const parsed = JSON.parse(raw || "");
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

export const readActiveFoldersStore = () => {
  const parsed = safeParse(localStorage.getItem(ACTIVE_FOLDERS_KEY), {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? parsed
    : {};
};

export const writeActiveFoldersStore = (store) => {
  localStorage.setItem(ACTIVE_FOLDERS_KEY, JSON.stringify(store || {}));
  return store || {};
};

export const readArchivedFolders = () => {
  const parsed = safeParse(localStorage.getItem(ARCHIVED_FOLDERS_KEY), []);
  return Array.isArray(parsed) ? parsed : [];
};

export const writeArchivedFolders = (list) => {
  const next = Array.isArray(list) ? list : [];
  localStorage.setItem(ARCHIVED_FOLDERS_KEY, JSON.stringify(next));
  return next;
};

export const readPlanFloors = () => {
  const parsed = safeParse(localStorage.getItem(FLOORS_KEY), []);
  return Array.isArray(parsed) ? parsed : [];
};

export const resolveLayoutLabels = (layoutKey = "", folder = {}) => {
  const [floorId = "", salleId = ""] = String(layoutKey || "").split(":");
  const floors = readPlanFloors();
  const floor = floors.find((item) => item.id === floorId);
  const salle = (floor?.salles || []).find((item) => item.id === salleId);
  return {
    floorId: floorId || folder.floorId || "",
    salleId: salleId || folder.salleId || "",
    floorName: folder.floorName || floor?.name || floorId || "—",
    salleName: folder.salleName || salle?.name || salleId || "—",
  };
};

/**
 * Move a setup from active folders into the archived list.
 * @returns {{ ok: boolean, reason?: string, entry?: object }}
 */
export const archivePlanFloorSetup = (folderId) => {
  if (!folderId) return { ok: false, reason: "missing" };

  const store = readActiveFoldersStore();
  let layoutKey = "";
  let folder = null;

  for (const [key, list] of Object.entries(store)) {
    const match = (Array.isArray(list) ? list : []).find(
      (item) => item.id === folderId
    );
    if (match) {
      layoutKey = key;
      folder = match;
      break;
    }
  }

  if (!folder || !layoutKey) return { ok: false, reason: "not_found" };

  const labels = resolveLayoutLabels(layoutKey, folder);
  const entry = {
    ...folder,
    isFolder: true,
    layoutKey,
    floorId: labels.floorId,
    salleId: labels.salleId,
    floorName: labels.floorName,
    salleName: labels.salleName,
    archivedAt: new Date().toISOString(),
  };

  writeActiveFoldersStore({
    ...store,
    [layoutKey]: (store[layoutKey] || []).filter((item) => item.id !== folderId),
  });

  const archived = readArchivedFolders().filter((item) => item.id !== folderId);
  writeArchivedFolders([entry, ...archived]);

  return { ok: true, entry, layoutKey };
};

/**
 * Restore an archived setup into the active folders store.
 * @returns {{ ok: boolean, reason?: string, entry?: object, layoutKey?: string }}
 */
export const restorePlanFloorSetup = (folderId) => {
  if (!folderId) return { ok: false, reason: "missing" };

  const archived = readArchivedFolders();
  const entry = archived.find((item) => item.id === folderId);
  if (!entry) return { ok: false, reason: "not_found" };

  const layoutKey =
    entry.layoutKey ||
    `${entry.floorId || ""}:${entry.salleId || ""}`;
  if (!layoutKey.includes(":")) return { ok: false, reason: "invalid_layout" };

  const store = readActiveFoldersStore();
  const list = Array.isArray(store[layoutKey]) ? store[layoutKey] : [];

  if (list.length >= MAX_FOLDERS_PER_SALLE) {
    return { ok: false, reason: "limit" };
  }

  const name = String(entry.name || "").trim().toLowerCase();
  if (
    name &&
    list.some(
      (item) => String(item.name || "").trim().toLowerCase() === name
    )
  ) {
    return { ok: false, reason: "name_exists" };
  }

  const { archivedAt, layoutKey: _lk, ...rest } = entry;
  const restored = {
    ...rest,
    isFolder: true,
    updatedAt: new Date().toISOString(),
  };

  writeActiveFoldersStore({
    ...store,
    [layoutKey]: [...list, restored],
  });
  writeArchivedFolders(archived.filter((item) => item.id !== folderId));

  return { ok: true, entry: restored, layoutKey };
};

/**
 * Permanently delete an archived setup.
 */
export const deleteArchivedPlanFloorSetup = (folderId) => {
  if (!folderId) return { ok: false, reason: "missing" };
  const archived = readArchivedFolders();
  const exists = archived.some((item) => item.id === folderId);
  if (!exists) return { ok: false, reason: "not_found" };
  writeArchivedFolders(archived.filter((item) => item.id !== folderId));
  return { ok: true };
};

export { MAX_FOLDERS_PER_SALLE, ARCHIVED_FOLDERS_KEY, ACTIVE_FOLDERS_KEY };
